import { useEditor, useNode, UserComponent } from '@craftjs/core';
import {
  findQueries,
  getExpressionFunctionCachedResult,
  isSimpleTimeFormat,
  QueryWithParameters,
} from '@lmt/expressions';
import {
  DataBlockFieldDto,
  DataBlockTypeDto,
  DataBlockVariant,
  DataType,
  DoctorType,
  DocumentQueryDto,
  MergeFieldQueryDto,
  MergeFieldQueryType,
  NumberSource,
  QueryFactory,
  useEnabledDataBlockTypes,
} from '@lmt/shared-types';
import { Field } from '@lmt/unicorn-shared/app/components/common/Field';
import { CheckBox } from '@lmt/unicorn-shared/app/components/common/ui-kit/CheckBox';
import { DatePicker } from '@lmt/unicorn-shared/app/components/common/ui-kit/inputs/date-time/DatePicker';
import { TimePicker } from '@lmt/unicorn-shared/app/components/common/ui-kit/inputs/date-time/TimePicker';
import { Input as CommonInput } from '@lmt/unicorn-shared/app/components/common/ui-kit/inputs/Input';
import { TextArea } from '@lmt/unicorn-shared/app/components/common/ui-kit/inputs/TextArea';
import { TagsViewer } from '@lmt/unicorn-shared/app/components/common/ui-kit/tags/TagsViewer';
import { InputSettings } from '@lmt/unicorn-shared/app/components/editor/components/blocks/Inputs/InputSettings';
import { ExternalPatientNumberBadge } from '@lmt/unicorn-shared/app/components/patients/ExternalPatientNumberBadge';
import { createId } from '@lmt/unicorn-shared/app/helpers/create-id';
import { formatISO } from 'date-fns';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useContextSelector } from 'use-context-selector';
import {
  convertStringToTime,
  fieldValueToDisplayString,
  TimeWithSecondsFormat,
} from '../../operations';
import { InputValuesContext, UIEditorData } from '../../UIEditorTypes';
import { BlockContainer } from './BlockContainer';
import { DictionaryInput } from './Inputs/DictionaryInput';
import { DefaultProps, DictionaryType, MandatoryProps } from './Inputs/types';

import styles from './UIEditorBlocks.module.scss';
import { EditableTextInput } from './Inputs/EditableTextInput';
import { EditableNumberInput } from './Inputs/EditableNumberInput';
import { InputDefaultProps } from './Inputs/InputDefaultConfig';
import { UIEditorDataObjectValue } from '../../UIEditorTypes';
import { executeExpression } from '@lmt/expressions/src/expressions/executor';
import { Error } from '../../../common/Error';

import {
  ExpressionDataFunction,
  ExpressionFunctionsCachedResult,
  ExpressionFunctionsDto,
} from '@lmt/expressions/src/expressions/types';
import { useQueryClient } from '@tanstack/react-query';
import {
  CustomDictionaryName,
  getFromCacheOrRequest,
  useDictionaryById,
} from '@lmt/unicorn-shared/app/api/custom-hooks';
import { isQueryIdentifiersEqual } from '../../../documents/types';
import { postProcessDataBlockFields } from '@lmt/expressions/src/expressions/helpers/post-process';
import _ from 'lodash';
import { format, parse } from 'date-fns';
import { getLocale } from '@lmt/expressions/src/expressions/helpers/locale';
import { getDateFormat } from '@lmt/expressions/src/expressions/helpers/date-helper';
import {
  caseHardcodedFields,
  IHardcodedField,
  patientHardcodedFields,
  visitHardcodedFields,
} from '../../../measurements/hardcoded-fields';
import { useClaims } from '@lmt/unicorn-shared/app/features/auth/operations';
import { useGlobalForms } from '../../../global-forms/useGlobalForms';
import i18next from 'i18next';
import { isEmpty } from '../../../measurements/copy-datablock-field/common';
import { getDataBlockDictionaries } from '../../../documents/expressions/data-cache';

export const Input: UserComponent<MandatoryProps & Partial<DefaultProps>> =
  React.memo(function Input(props: MandatoryProps & Partial<DefaultProps>) {
    const { connectors } = useNode((state) => ({
      dragged: state.events.dragged,
    }));
    const i18n = useTranslation();
    const claims = useClaims();
    const globalForms = useGlobalForms();
    const { connect, drag } = connectors!;
    const {
      size,
      dataKey,
      dataType,
      isLabel,
      label: propsLabel,
      isEditable,
      isMultiline,
      isShared,
      dictionaryType,
      expression,
    } = {
      ...InputDefaultProps,
      ...props,
    };
    const dataBlockType = useContextSelector(
      InputValuesContext,
      (v) => v.dataBlockType,
    );
    const dataBlock = useContextSelector(
      InputValuesContext,
      (v) => v.dataBlock,
    );
    const dataBlockTypeName = useContextSelector(
      InputValuesContext,
      (v) => v.dataBlockTypeName,
    );

    const isErrorVisible = useContextSelector(
      InputValuesContext,
      (v) => v.showErrors,
    );
    const isMandatory = props.isRequired ?? props.isMandatory;

    const label =
      propsLabel === ''
        ? props.isShared
          ? i18n.t(
              'data-block-fields:' +
                dataBlockTypeName.split('.', 1)[0] +
                '.shared' +
                '.' +
                dataKey.toLowerCase(),
              dataKey,
            )
          : i18n.t(
              'data-block-fields:' +
                dataBlockTypeName +
                '.' +
                dataKey.toLowerCase(),
              dataKey,
            )
        : propsLabel;
    const placeholder = props.isShared
      ? i18n.t(
          'data-block-fields:' +
            dataBlockTypeName.split('.', 1)[0] +
            '.shared' +
            '.' +
            dataKey.toLowerCase(),
          dataKey,
        )
      : i18n.t(
          'data-block-fields:' +
            dataBlockTypeName +
            '.' +
            dataKey.toLowerCase(),
          dataKey,
        );
    const isInputValuesLoaded = useContextSelector(
      InputValuesContext,
      (v) => v.values !== undefined,
    );
    const inputValue = useContextSelector(
      InputValuesContext,
      isShared
        ? (v) => (v.sharedValues ? v.sharedValues[dataKey] : undefined)
        : (v) => (v.values ? v.values[dataKey] : undefined),
    );
    const inputValues = useContextSelector(InputValuesContext, (v) => v.values);
    const sharedValues = useContextSelector(
      InputValuesContext,
      (v) => v.sharedValues,
    );

    const setDataBlockFieldValue = useContextSelector(
      InputValuesContext,
      isShared ? (v) => v.setSharedValues : (v) => v.setValues,
    );
    const setDataBlockFieldError = useContextSelector(
      InputValuesContext,
      (v) => v.setErrors,
    );

    const isOnlyView = useContextSelector(
      InputValuesContext,
      (v) => v.isOnlyView,
    );

    const { enabled: isInDesigner } = useEditor((state) => ({
      enabled: state.options.enabled,
    }));

    /* this is to handle special case of displaying CaseNumber with badge */
    let numberSource: NumberSource | undefined = undefined;
    if (isShared && dataKey === 'Number') {
      numberSource = useContextSelector(InputValuesContext, (v) =>
        v.sharedValues ? (v.sharedValues['NumberSource'] as any) : undefined,
      )!;
    }
    const [text, setText] = useState<UIEditorDataObjectValue>('');
    const [error, setError] = useState<string>('');
    const [expressionResult, setExpressionResult] = useState<any>();
    const [expressionQueries, setExpressionQueries] =
      useState<QueryWithParameters[]>();
    const [baseQueries, setBaseQueries] = useState<DocumentQueryDto[]>();
    const [fieldsValues, setFieldsValues] =
      useState<ExpressionFunctionsCachedResult>();
    const defaultValue = '';
    const queryClient = useQueryClient();

    const dataBlockFields = QueryFactory.DataBlockFieldQuery.useGetQuery(
      {
        dataBlockTypeId: dataBlock?.typeId,
      },
      {
        enabled: !!dataBlock?.typeId,
      },
    );
    const dataBlockTypes = useEnabledDataBlockTypes();
    const dataBlockTypesDictionary = dataBlockTypes.reduce(
      (result, item) => {
        result[item.value] = item;
        return result;
      },
      {} as Record<string, DataBlockTypeDto>,
    );
    const sharedBlockId = dataBlockTypes.find(
      (x) =>
        x.variant === DataBlockVariant.SharedFields &&
        ((dataBlockType?.variant === DataBlockVariant.Visit &&
          x.value === 'Visit') ||
          (dataBlockType?.variant === DataBlockVariant.Case &&
            x.value === 'Case') ||
          (dataBlockType?.variant === DataBlockVariant.Patient &&
            x.value === 'Patient')),
    )?.id;
    const sharedFields = QueryFactory.DataBlockFieldQuery.useGetQuery(
      sharedBlockId,
      {
        enabled: !!sharedBlockId,
      },
    );

    useEffect(() => {
      if (expression) {
        if (!expressionQueries) void getQueries();
        if (!baseQueries) void getBaseQueries();
      }

      async function getQueries() {
        setExpressionQueries(await findQueries(expression));
      }

      async function getBaseQueries() {
        setBaseQueries(
          await getFromCacheOrRequest(
            queryClient,
            QueryFactory.MergeFieldQuery.getQueriesQueryKey(),
            () => QueryFactory.MergeFieldClient.getQueries(),
          ),
        );
      }
    }, [expression]);

    const getFieldsValues = async () => {
      if (!expressionQueries || !baseQueries) return;

      const expressionFunctionsData: ExpressionFunctionsDto[] = [];
      const promises = expressionQueries.map(async (query) => {
        if (
          query.query.queryType === MergeFieldQueryType.DataBlockQuery &&
          !dataBlock?.id
        )
          return;
        const queryIdentifier: MergeFieldQueryDto = {
          queryType: query.query.queryType,
          dataBlockTypeId: query.query.dataBlockTypeName
            ? dataBlockTypesDictionary[query.query.dataBlockTypeName]?.id
            : null,
          selectedVisits: query.query.selectedVisits,
        };
        const data =
          (await QueryFactory.MergeFieldClient.executeQuery({
            query: queryIdentifier,
            filter: null,
            limit: query.limit,
            secondArgument: query.expressionFunctionArgument2,
            patientId: dataBlock?.patientId || 0,
            caseId: dataBlock?.caseId || null,
            visitId: dataBlock?.visitId || null,
            dataBlockId: dataBlock?.id || null,
          })) ?? {};

        const currentBaseQuery = baseQueries!.find((x) =>
          isQueryIdentifiersEqual(x.queryIdentifier, queryIdentifier),
        )!;

        expressionFunctionsData.push({
          data: data,
          query: query,
          currentBaseQuery: currentBaseQuery,
        });
      });

      await Promise.all(promises);

      setFieldsValues(
        getExpressionFunctionCachedResult(expressionFunctionsData),
      );
    };

    const executeCurrentExpression = async () => {
      if (!fieldsValues) return;

      const isExecuteExpression = shouldExecuteExpression(
        fieldsValues,
        inputValues,
        sharedValues,
      );

      const preparedFieldsValues = _.cloneDeep(fieldsValues);
      if (isExecuteExpression) {
        if (dataBlockType && sharedFields) {
          let sharedBlockName: ExpressionDataFunction | undefined = undefined;
          let queryType: MergeFieldQueryType | undefined = undefined;
          switch (dataBlockType.variant) {
            case DataBlockVariant.Visit:
              sharedBlockName = ExpressionDataFunction.visit;
              queryType = MergeFieldQueryType.VisitQuery;
              break;
            case DataBlockVariant.Case:
              sharedBlockName = ExpressionDataFunction.Case;
              queryType = MergeFieldQueryType.CaseQuery;
              break;
            case DataBlockVariant.Patient:
              sharedBlockName = ExpressionDataFunction.patient;
              queryType = MergeFieldQueryType.PatientQuery;
              break;
          }

          if (sharedBlockName && queryType) {
            await prepareExpressionValues(
              sharedValues,
              sharedFields.data,
              preparedFieldsValues,
              sharedBlockName,
              '',
              queryType,
            );
          }
        }

        await prepareExpressionValues(
          inputValues,
          dataBlockFields.data,
          preparedFieldsValues,
          ExpressionDataFunction.block,
          dataBlockType?.value ?? '',
          MergeFieldQueryType.DataBlockQuery,
        );
      }

      let result = isExecuteExpression
        ? executeExpression(expression, preparedFieldsValues)?.toString()
        : undefined;

      if (result && dataType === DataType.DateTime) {
        result = new Date(result).toISOString();
      }

      if (result && dataType === DataType.DateOnly) {
        const dateFormat = getDateFormat();
        const newDate = parse(result, dateFormat, new Date(), {
          locale: getLocale(),
        });

        result = newDate.toDateString();
      }

      if (
        Object.is(result, expressionResult) ||
        (result === text && Object.is(result, inputValue))
      ) {
        return;
      }

      setExpressionResult(result);
      if (dataType === DataType.Number && !isFinite(result)) {
        if (isMandatory) {
          setError(i18n.t('uikit.inputs.calculation_error'));
        }
        setText(defaultValue);
        setDataBlockFieldValue?.(props.dataKey, defaultValue);
      } else {
        setError('');
        setText(result);
        setDataBlockFieldValue?.(props.dataKey, result);
      }
    };

    const getPreparedDictionaryValues = async (
      dictionaryId: number | undefined | null,
    ) => {
      const dictionaryValues = await getFromCacheOrRequest(
        queryClient,
        QueryFactory.DataBlockDictionaryItemQuery.getQueryKey(
          dictionaryId ?? undefined,
        ),
        () =>
          QueryFactory.DataBlockDictionaryItemClient.get(
            dictionaryId ?? undefined,
            true,
          ),
      );

      let preparedDictionaryValues: any[] = [];
      preparedDictionaryValues = dictionaryValues.data.map(
        (dictionaryValue) => {
          let preparedValue: any = {};
          for (const [key, value] of Object.entries(dictionaryValue)) {
            if (key === 'values') {
              continue;
            }
            const newKey = key.charAt(0).toUpperCase() + key.slice(1);
            preparedValue[newKey] = value;
          }

          preparedValue = { ...preparedValue, ...dictionaryValue.values };
          return preparedValue;
        },
      );

      return preparedDictionaryValues;
    };

    const getDictionaryValues = async (
      dictionaryId: number | null | undefined,
    ) => {
      const dictionaries = await getDataBlockDictionaries(queryClient);
      const dictionary = dictionaries.find((d) => d.id === dictionaryId);

      const dictionaryValues = await getPreparedDictionaryValues(dictionaryId);

      if (
        dictionary?.columns &&
        dictionary.columns.some((x) => x.dataType === DataType.Dictionary)
      ) {
        const promises = dictionary.columns.map(async (column) => {
          if (column.dataType !== DataType.Dictionary) return;
          const referenceDictionaryValues = await getPreparedDictionaryValues(
            column.referenceDictionaryId,
          );

          dictionaryValues.map((dicValue) => {
            if (dicValue && dicValue.hasOwnProperty(column.title)) {
              dicValue[column.title] = referenceDictionaryValues.find(
                (refDicValue) => refDicValue.Id === dicValue[column.title],
              );
            }
          });
        });

        await Promise.all(promises);
      }

      return dictionaryValues;
    };

    /// If we have all fields with a numeric data type and they are all empty,
    /// then we should not calculate the expression.
    /// In another case, we need to calculate the expression
    const shouldExecuteExpression = (
      allFieldsValues: ExpressionFunctionsCachedResult,
      valuesBlock: UIEditorData | undefined,
      valuesShared: UIEditorData | undefined,
    ) => {
      if (!expressionQueries?.length) {
        return true;
      }
      const someExpressionValuesExist = expressionQueries?.some((query) => {
        let values: UIEditorData;
        if (query.expressionFunctionName === ExpressionDataFunction.block) {
          values = {
            ...allFieldsValues[query.expressionFunctionName][
              query.expressionFunctionArgument
            ],
            ...valuesBlock,
          };
        } else {
          values = {
            ...allFieldsValues[query.expressionFunctionName][
              query.expressionFunctionArgument
            ],
            ...valuesShared,
          };
        }

        const fields = baseQueries!.find((x) =>
          isQueryIdentifiersEqual(x.queryIdentifier, {
            queryType: query.query.queryType,
            dataBlockTypeId:
              query.query.queryType === MergeFieldQueryType.DataBlockQuery
                ? dataBlock?.typeId || null
                : null,
          }),
        );

        if (
          query.fields.every((fieldName) =>
            fields?.queryFields.some(
              (field) =>
                field.dataField === fieldName &&
                field.dataType === DataType.Number,
            ),
          )
        ) {
          return query.fields.some(
            (fieldName) =>
              values[fieldName] !== '' &&
              values[fieldName] !== null &&
              values[fieldName] !== undefined &&
              !isNaN(values[fieldName] as number),
          );
        } else {
          return true;
        }
      });

      return someExpressionValuesExist;
    };

    const prepareExpressionValues = async (
      values: UIEditorData | undefined,
      fields: DataBlockFieldDto[] | undefined,
      allFieldsValues: ExpressionFunctionsCachedResult,
      key: ExpressionDataFunction,
      blockName: string,
      queryType: MergeFieldQueryType,
    ) => {
      const blockValues = { ...values };
      if (values !== undefined) {
        let config: Record<string, IHardcodedField> | undefined = undefined;
        switch (key) {
          case ExpressionDataFunction.visit:
            config = visitHardcodedFields;
            break;
          case ExpressionDataFunction.Case:
            config = caseHardcodedFields;
            break;
          case ExpressionDataFunction.patient:
            config = patientHardcodedFields;
            break;
        }

        if (config !== undefined) {
          Object.keys(config).forEach((key) => {
            const field = config[key];

            if (field.customValuesFieldName in values) {
              delete blockValues[field.customValuesFieldName];
              blockValues[key] = values[field.customValuesFieldName];
            }
          });
        }
      }
      const fullInputValues = {
        ...allFieldsValues[key][blockName],
        ...blockValues,
      };

      const promises = fields?.map(async (field) => {
        switch (field.dataType) {
          case DataType.Number:
            if (
              fullInputValues[field.name] === '' ||
              fullInputValues[field.name] === null ||
              fullInputValues[field.name] === undefined ||
              isNaN(fullInputValues[field.name])
            ) {
              if (dataType === DataType.Text) {
                fullInputValues[field.name] = null;
              } else {
                fullInputValues[field.name] = 0;
              }
            }
            break;
          case DataType.DateOnly:
            if (fullInputValues[field.name]) {
              fullInputValues[field.name] = format(
                new Date(fullInputValues[field.name]),
                'yyyy-MM-dd',
              );
            }
            break;
          case DataType.Dictionary:
            if (
              (Array.isArray(fullInputValues[field.name]) &&
                fullInputValues[field.name].length) ||
              !isNaN(fullInputValues[field.name])
            ) {
              const preparedDictionaryValues = await getDictionaryValues(
                field.dictionaryId,
              );

              if (Array.isArray(fullInputValues[field.name])) {
                fullInputValues[field.name] = fullInputValues[field.name].map(
                  (value: any) => {
                    return (
                      preparedDictionaryValues.find(
                        (dictionaryValue) => dictionaryValue.Id === value,
                      ) ?? value
                    );
                  },
                );
              } else {
                fullInputValues[field.name] =
                  preparedDictionaryValues.find(
                    (dictionaryValue) =>
                      dictionaryValue &&
                      dictionaryValue.Id === fullInputValues[field.name],
                  ) ?? fullInputValues[field.name];
              }
            }
            break;
        }
      });

      if (promises?.length) await Promise.all(promises);

      postProcessDataBlockFields(
        allFieldsValues,
        key,
        blockName,
        fullInputValues,
        baseQueries!.find((x) =>
          isQueryIdentifiersEqual(x.queryIdentifier, {
            queryType,
            dataBlockTypeId:
              queryType === MergeFieldQueryType.DataBlockQuery
                ? dataBlock?.typeId || null
                : null,
          }),
        )!,
      );
    };

    useEffect(() => {
      if (!isInDesigner && !isOnlyView && expression) {
        void getFieldsValues();
      }
    }, [isInDesigner, isOnlyView, expression, expressionQueries, baseQueries]);

    useEffect(() => {
      if (!isInDesigner && !isOnlyView && expression && fieldsValues) {
        void executeCurrentExpression();
      }
    }, [
      isInDesigner,
      isOnlyView,
      expression,
      fieldsValues,
      inputValues,
      sharedValues,
    ]);

    useEffect(
      function updateDisplayedTextValue() {
        if (isInDesigner) {
          setText('');
        } else {
          if (inputValue !== undefined) {
            setText(inputValue);
          } else if (dataType === DataType.Boolean && isMandatory) {
            setText('false');
            setDataBlockFieldValue?.(dataKey, 'false');
          } else if (
            dataType === DataType.DateTime &&
            (dataBlockType?.variant === DataBlockVariant.Visit ||
              dataBlockType?.variant === DataBlockVariant.Case) &&
            dataKey.toLocaleLowerCase() === 'date'
          ) {
            // If dataBlockType belongs to visit and field is date and it is default -
            // we need to insert current date of creation
            const newDate = new Date().toISOString();
            setText(newDate);
            setDataBlockFieldValue?.(props.dataKey, newDate);
          } else {
            setText('');
          }
        }
      },
      [isInDesigner, inputValue, dataType, dictionaryType],
    );

    const numberFormat = useMemo(
      function verifyFormat() {
        const format = props.format;
        if (!isInDesigner && format) {
          switch (dataType) {
            case DataType.Number:
              return format === '0' || format.match(/^0.0+/) ? format : '';

            case DataType.TimeOnly:
              return isSimpleTimeFormat(format) ? format : '';

            case DataType.DateTime:
            case DataType.DateOnly:
              return format;

            default:
              return '';
          }
        } else {
          return '';
        }
      },
      [props.format, isInDesigner, isEditable],
    );

    useEffect(() => {
      if (isInputValuesLoaded && !isOnlyView) {
        if (isEditable || (!!expression && !error)) {
          if (isMandatory) {
            if (text === '' || text === null || text === undefined) {
              setError(i18n.t('errors.required'));
              return;
            }
          }
          if (
            dataType === DataType.Number &&
            text &&
            isNaN(parseFloat(text as string))
          ) {
            setError(i18n.t('errors.not_a_number'));
            return;
          }
          setError('');
        }
      }
    }, [text, isInputValuesLoaded, isOnlyView]);

    useEffect(() => {
      setDataBlockFieldError?.(dataKey, error);
    }, [error]);

    function getDateFromValue(value: UIEditorDataObjectValue): Date | null {
      return value === null || value === undefined || value === ''
        ? null
        : value instanceof Date
          ? (value as unknown as Date)
          : new Date(value as string);
    }

    const notEditableInput = (placeholder: string) => (
      <CommonInput
        placeholder={placeholder}
        readOnly={true}
        value={(text as string) ?? null}
      />
    );

    function showOnlyViewValue(fieldDataType: DataType, fieldValue: string) {
      const result = fieldValueToDisplayString(
        fieldDataType,
        fieldValue,
        numberFormat,
      );
      return result ?? '-';
    }

    const validationError = useMemo(() => {
      return isErrorVisible ? error : '';
    }, [isErrorVisible, error]);

    const booleanId = useRef(createId());
    const InputBoolean = dataType === DataType.Boolean && (
      <CheckBox
        name={booleanId.current}
        title={label}
        checked={text === 'true' || text === true}
        disabled={!isEditable || isOnlyView || isInDesigner}
        onChange={(e) => {
          const value = e.target.checked;
          setText(value ? 'true' : 'false');
          setDataBlockFieldValue?.(props.dataKey, value);
        }}
      />
    );

    const EditableInputNumber = isEditable && dataType === DataType.Number && (
      <EditableNumberInput
        {...props}
        text={text as number}
        setText={setText}
        setDataBlockFieldValue={setDataBlockFieldValue}
        error={validationError}
        popperClassName={styles.inputTemplatePopper}
      />
    );

    const EditableInputTextWithTags = isEditable &&
      dataType === DataType.Text && (
        <EditableTextInput
          {...props}
          text={text as string}
          setText={setText}
          setDataBlockFieldValue={setDataBlockFieldValue}
          error={validationError}
          popperClassName={styles.inputTemplatePopper}
        />
      );

    const FormattedInputNumber =
      !isEditable &&
      numberFormat &&
      (isMultiline ? (
        <TextArea
          readOnly={!isEditable}
          value={fieldValueToDisplayString(DataType.Number, text, numberFormat)}
        />
      ) : (
        <CommonInput
          readOnly={!isEditable}
          value={fieldValueToDisplayString(DataType.Number, text, numberFormat)}
        />
      ));

    const InputDateTime = dataType === DataType.DateTime && (
      <DatePicker
        withTime={true}
        value={getDateFromValue(text)}
        error={validationError !== ''}
        errorText={validationError}
        disableKeyboardMode={false}
        allowToSelectYear={true}
        doNotAdjustTimeAccordingToClinicTimezone={true}
        onDateChanged={(newDate) => {
          setDataBlockFieldError?.(props.dataKey, '');
          if (newDate) {
            setText(formatISO(newDate));
            setDataBlockFieldValue?.(props.dataKey, newDate as any);
          } else {
            setText('');
            setDataBlockFieldValue?.(props.dataKey, undefined);
          }
        }}
        onInvalidDate={() => {
          setDataBlockFieldError?.(props.dataKey, 'invalid');
        }}
      />
    );

    const InputDate =
      dataType === DataType.DateOnly &&
      (isEditable ? (
        <DatePicker
          value={getDateFromValue(text)}
          error={validationError !== ''}
          errorText={validationError}
          disableKeyboardMode={false}
          allowToSelectYear={true}
          onDateChanged={(newDate) => {
            setDataBlockFieldError?.(props.dataKey, '');
            if (newDate) {
              // We always parse the date when it changes so we disable keyboard mode
              setText(formatISO(newDate));
              setDataBlockFieldValue?.(props.dataKey, formatISO(newDate));
            } else {
              setText('');
              setDataBlockFieldValue?.(props.dataKey, undefined);
            }
          }}
          onInvalidDate={() => {
            setDataBlockFieldError?.(props.dataKey, 'invalid');
          }}
        />
      ) : (
        <CommonInput
          readOnly={!isEditable}
          value={fieldValueToDisplayString(
            DataType.DateOnly,
            text,
            numberFormat,
          )}
        />
      ));

    const InputTime =
      dataType === DataType.TimeOnly &&
      (isEditable ? (
        numberFormat && numberFormat !== TimeWithSecondsFormat ? (
          <CommonInput
            value={fieldValueToDisplayString(
              DataType.TimeOnly,
              text,
              numberFormat,
            )}
            errorText={validationError}
            type="number"
            onChange={(e) => {
              const newValue = e.target?.value;
              if (newValue === undefined) return;
              const newTimeValue = convertStringToTime(newValue, numberFormat);
              if (newTimeValue === null) return;
              setText(newTimeValue as any);
              setDataBlockFieldValue?.(props.dataKey, newTimeValue as any);
            }}
          />
        ) : (
          <TimePicker
            error={validationError !== ''}
            errorText={validationError}
            timeInMills={
              text === undefined || text === null || text === ''
                ? null
                : parseInt(text as string)
            }
            onTimeChanged={(newTime) => {
              setDataBlockFieldError?.(props.dataKey, '');
              if (newTime || newTime === 0) {
                setText(newTime.toString());
                setDataBlockFieldValue?.(props.dataKey, newTime.toString());
              } else {
                setText('');
                setDataBlockFieldValue?.(props.dataKey, undefined);
              }
            }}
            onInvalidTime={() => {
              setDataBlockFieldError?.(props.dataKey, 'invalid');
            }}
            className={styles.input}
          />
        )
      ) : (
        <CommonInput
          readOnly={true}
          value={fieldValueToDisplayString(
            DataType.TimeOnly,
            text,
            numberFormat,
          )}
        />
      ));

    if (isInDesigner) {
      return (
        <div
          className={styles.container}
          style={{
            width: size,
          }}
          ref={(ref) => connect(drag(ref!))}
        >
          <div
            className={styles.elementContainer}
            data-app-field={label}
            title={label}
          >
            <div className={styles.title}>
              {!InputBoolean && isLabel ? label : ' '}
            </div>
            {InputBoolean || notEditableInput(placeholder)}
          </div>
        </div>
      );
    }

    // This is used to auto select value when add new item
    const setDictionaryValue = useCallback(
      (value: any) => {
        if (isEmpty(value)) {
          return;
        }
        if (
          !isEmpty(inputValue) &&
          Array.isArray(inputValue) &&
          (dictionaryType === DictionaryType.CheckboxGroup ||
            dictionaryType === DictionaryType.CheckboxGroupWithCustomOption)
        ) {
          setDataBlockFieldValue?.(props.dataKey, [...inputValue, value]);
        } else {
          setDataBlockFieldValue?.(props.dataKey, [value]);
        }
      },
      [inputValue],
    );

    const dictionary = useDictionaryByFieldId(
      props.dataType,
      props.dataBlockFieldId,
      props.dictionaryTitle,
    );

    const DictionaryComponent =
      props.dataType === DataType.Dictionary ? (
        <DictionaryInput
          {...props}
          isMandatory={isMandatory}
          dictionaryTitle={props.dictionaryTitle}
          dictionary={dictionary}
          fieldValue={inputValue}
          isFieldValueLoaded={isInputValuesLoaded}
          setValue={setDataBlockFieldValue}
          isOnlyView={isOnlyView}
          setError={setDataBlockFieldError}
          key={isOnlyView.toString()}
          isErrorVisible={isErrorVisible}
        />
      ) : null;

    const createDictionaryItemLink = useMemo(() => {
      if (
        !props.isDictionaryItemAdding ||
        (!claims.permissions.doctorManagement &&
          !claims.permissions.dictionariesManagement) ||
        !dictionary ||
        isOnlyView ||
        !isEditable ||
        props.dictionaryTitle ||
        dictionary.isReadOnly
      ) {
        return;
      }
      if (!dictionary.isEditable) {
        if (!claims.permissions.doctorManagement) {
          return;
        }
        if (dictionary.title === CustomDictionaryName.Doctors) {
          return newItemLinkProps(async () => {
            const doctor = await globalForms.openDoctorCreating(
              DoctorType.External,
            );
            setDictionaryValue(doctor?.id);
          });
        }
        if (dictionary.title === CustomDictionaryName.ClinicDoctors) {
          return newItemLinkProps(async () => {
            const doctor = await globalForms.openDoctorCreating(
              DoctorType.Clinic,
              false,
            );
            setDictionaryValue(doctor?.id);
          });
        }
        return;
      }
      if (!claims.permissions.dictionariesManagement) {
        return;
      }
      if (dictionary.isExternal) {
        if (dictionary.title === 'Visit Types') {
          return newItemLinkProps(async () => {
            const visitType = await globalForms.openVisitTypeCreating();
            if (visitType) {
              // Need to find created visit type dictionary item to select it
              const visitTypeItems = await getFromCacheOrRequest(
                queryClient,
                QueryFactory.DataBlockDictionaryItemQuery.getQueryKey(
                  dictionary.id,
                ),
                () =>
                  QueryFactory.DataBlockDictionaryItemClient.get(dictionary.id),
              );
              const visitTypeItem = visitTypeItems.data.find(
                (x) => x.value === visitType?.value,
              );
              setDictionaryValue(visitTypeItem?.id);
            }
          });
        }
        return;
      }
      return newItemLinkProps(async () => {
        const dictionaryValue =
          await globalForms.openCreateCustomDictionaryItem(dictionary.id);
        setDictionaryValue(dictionaryValue?.id);
      });
    }, [isEditable, isOnlyView, dictionary?.id, setDictionaryValue]);

    const shouldHideField =
      props.isHiddenWhenEmpty && isEmpty(inputValue ?? text) && isOnlyView;

    return shouldHideField ? null : (
      <BlockContainer size={size}>
        <Field
          title={isLabel && !InputBoolean ? label : ''}
          titleClassName={styles.label}
          linkProps={
            numberSource === NumberSource.External
              ? externalNumberAsLinkProps
              : !!DictionaryComponent
                ? createDictionaryItemLink
                : undefined
          }
          required={isMandatory && !isOnlyView && !InputBoolean}
        >
          {DictionaryComponent ||
            InputBoolean ||
            (isOnlyView || !isEditable ? (
              <>
                <div
                  data-error={!!validationError}
                  className={styles.text}
                  data-test-id="field-value"
                >
                  {dataType === DataType.Text ? (
                    <TagsViewer
                      text={showOnlyViewValue(dataType, text as string)}
                    />
                  ) : (
                    showOnlyViewValue(dataType, text as string)
                  )}
                </div>
                <Error className={styles.textError} error={validationError} />
              </>
            ) : (
              FormattedInputNumber ||
              InputDateTime ||
              InputDate ||
              InputTime ||
              EditableInputNumber ||
              EditableInputTextWithTags ||
              notEditableInput('')
            ))}
        </Field>
      </BlockContainer>
    );
  });

Input.craft = {
  defaultProps: InputDefaultProps,
  related: {
    settings: InputSettings,
  },
};

const externalNumberAsLinkProps = {
  title: <ExternalPatientNumberBadge />,
};

const newItemLinkProps = (onClick: () => void) => {
  return {
    title: i18next.t('editor.blocks.add_new_item'),
    onClick: onClick,
  };
};

function useDictionaryByFieldId(
  dataType: DataType,
  fieldId?: number,
  dictionaryTitle?: string | null,
) {
  if (dataType !== DataType.Dictionary || !!dictionaryTitle) {
    return;
  }

  const fieldQuery = QueryFactory.DataBlockFieldQuery.useGetByIdQuery(
    fieldId ?? 0,
    {
      enabled: !!fieldId,
    },
  );

  return useDictionaryById(fieldQuery.data?.dictionaryId);
}
